export type TourheroRoomTerm = 'unspecified' | 'included' | 'supplement' | 'waived'

export interface TourheroSpot {
  count: number | null
  chargeCents: number | null
  singleRoomTerm?: TourheroRoomTerm | null
  singleRoomSupplementCents?: number | null
}

export interface TourheroColumnRoom {
  singleRoomTerm: TourheroRoomTerm
  singleRoomSupplementCents: number | null
}

export interface TourheroTier {
  min: number | null
  max: number | null
  chargeCents: number | null
  spots: TourheroSpot[]
}

export interface TourheroTerms {
  tiers: TourheroTier[]
  singleRoomTerm: TourheroRoomTerm
  singleRoomSupplementCents: number | null
  note: string | null
  spotRooms: TourheroColumnRoom[]
}

export interface TourheroPlacePricing {
  applicable: boolean
  status: 'open' | 'confirmed' | 'disputed'
  currency: string
  guestMin: number
  guestMax: number
  completeness: 'not_captured' | 'missing_terms' | 'complete'
  fullyLocked: boolean
  terms: TourheroTerms
  suppliedTerms: TourheroTerms
  proposal: TourheroTerms | null
  rejectionNote: string | null
  confirmedAt: string | null
  bands?: TourheroOperatorBand[]
}

export interface TourheroOperatorBand {
  min: number
  max: number
  operateRateCents: number | null
}

export const ROOM_OPTIONS: { value: TourheroRoomTerm; label: string }[] = [
  { value: 'unspecified', label: 'Not yet specified' },
  { value: 'included', label: 'Included in the charge' },
  { value: 'supplement', label: 'Additional supplement' },
  { value: 'waived', label: 'Supplement waived' },
]

export function emptyTerms(guestMin: number, guestMax: number): TourheroTerms {
  return {
    tiers: [{ min: guestMin, max: guestMax, chargeCents: null, spots: [] }],
    singleRoomTerm: 'unspecified',
    singleRoomSupplementCents: null,
    note: null,
    spotRooms: [],
  }
}

export function cloneTerms(terms: TourheroTerms | null | undefined, guestMin: number, guestMax: number): TourheroTerms {
  if (!terms?.tiers?.length) return emptyTerms(guestMin, guestMax)

  return {
    tiers: terms.tiers.map(tier => ({
      min: tier.min,
      max: tier.max,
      chargeCents: tier.chargeCents ?? null,
      spots: (tier.spots || []).flatMap(spot => {
        const copies = spot.count != null && spot.count > 1 ? spot.count : 1
        return Array.from({ length: copies }, () => ({
          count: 1,
          chargeCents: spot.chargeCents ?? null,
        }))
      }),
    })),
    singleRoomTerm: terms.singleRoomTerm || 'unspecified',
    singleRoomSupplementCents: terms.singleRoomSupplementCents ?? null,
    note: terms.note ?? null,
    spotRooms: terms.spotRooms?.length ? terms.spotRooms.map(room => ({ ...room })) : columnRoomsFromTiers(terms.tiers),
  }
}

type RangedTier = { min: number | null; max: number | null }

function clamp(value: number, floor: number, ceiling: number) {
  return Math.min(ceiling, Math.max(floor, value))
}

function blankAddedTier<T extends RangedTier>(tier: T, min: number, max: number): T {
  const added = { ...tier, min, max }
  if ('chargeCents' in added) (added as T & { chargeCents: number | null }).chargeCents = null
  if ('charge' in added) (added as T & { charge: string }).charge = ''
  if ('spots' in added) (added as T & { spots: TourheroSpot[] }).spots = []
  return added
}

export function appendContiguousTier<T extends RangedTier>(
  tiers: T[],
  guestMin: number,
  guestMax: number,
  lastLocked = false,
): T[] {
  const next = tiers.map(tier => ({ ...tier }))
  if (next.length === 0) return [blankAddedTier({ min: guestMin, max: guestMax } as T, guestMin, guestMax)]

  const last = next[next.length - 1]
  if (last.min == null || last.max == null) return next
  if (last.max < guestMax) {
    next.push(blankAddedTier(last, last.max + 1, guestMax))
    return next
  }
  if (lastLocked || last.max <= last.min) return next

  const split = last.min + Math.floor((last.max - last.min) / 2)
  last.max = split
  next.push(blankAddedTier(last, split + 1, guestMax))
  return next
}

export function syncTierBoundary<T extends RangedTier>(
  tiers: T[],
  index: number,
  edge: 'min' | 'max',
  guestMin: number,
  guestMax: number,
  lockedIndexes: ReadonlySet<number> = new Set(),
): T[] {
  const next = tiers.map(tier => ({ ...tier }))
  if (next.length === 0) return next

  if (!lockedIndexes.has(0) && next[0]) next[0].min = guestMin
  const lastIndex = next.length - 1
  if (!lockedIndexes.has(lastIndex) && next[lastIndex]) next[lastIndex].max = guestMax

  const tier = next[index]
  if (!tier || lockedIndexes.has(index) || tier.min == null || tier.max == null) return next

  if (edge === 'max' && index < lastIndex) {
    const ceiling = forwardCeiling(next, index, guestMax, lockedIndexes)
    if (tier.min > ceiling) return next
    tier.max = clamp(tier.max, tier.min, ceiling)
    if (lockedIndexes.has(index + 1)) return next
    cascadeForward(next, index, guestMax, lockedIndexes)
  }

  if (edge === 'min' && index > 0) {
    const previous = next[index - 1]
    if (lockedIndexes.has(index - 1)) {
      const floor = (previous.max ?? guestMin) + 1
      tier.min = clamp(tier.min, floor, tier.max)
      return next
    }
    const floor = (previous.min ?? guestMin) + 1
    if (floor > tier.max) return next
    tier.min = clamp(tier.min, floor, tier.max)
    previous.max = tier.min - 1
  }

  if (!lockedIndexes.has(0) && next[0]) next[0].min = guestMin
  if (!lockedIndexes.has(lastIndex) && next[lastIndex]) next[lastIndex].max = guestMax
  return next
}

function forwardCeiling<T extends RangedTier>(
  tiers: T[],
  index: number,
  guestMax: number,
  lockedIndexes: ReadonlySet<number>,
) {
  for (let cursor = index + 1; cursor < tiers.length; cursor += 1) {
    if (!lockedIndexes.has(cursor)) continue
    const lockedMin = tiers[cursor].min ?? guestMax
    return lockedMin - 1 - (cursor - index - 1)
  }

  return guestMax - (tiers.length - 1 - index)
}

function cascadeForward<T extends RangedTier>(
  tiers: T[],
  index: number,
  guestMax: number,
  lockedIndexes: ReadonlySet<number>,
) {
  for (let cursor = index; cursor < tiers.length - 1; cursor += 1) {
    const current = tiers[cursor]
    const following = tiers[cursor + 1]
    if (current.max == null || lockedIndexes.has(cursor + 1)) return

    following.min = current.max + 1
    if (cursor + 1 === tiers.length - 1) {
      following.max = guestMax
      if (following.min > guestMax) {
        following.min = guestMax
        current.max = guestMax - 1
      }
      return
    }
    if (following.max != null && following.min > following.max) following.max = following.min
  }
}

export function removeContiguousTier<T extends RangedTier>(
  tiers: T[],
  index: number,
  guestMin: number,
  guestMax: number,
): T[] {
  if (tiers.length <= 1) {
    const only = tiers[0] ? { ...tiers[0] } : ({ min: guestMin, max: guestMax } as T)
    only.min = guestMin
    only.max = guestMax
    return [only]
  }

  const next = tiers.filter((_, tierIndex) => tierIndex !== index).map(tier => ({ ...tier }))
  next[0].min = guestMin
  next[next.length - 1].max = guestMax
  for (let cursor = 0; cursor < next.length - 1; cursor += 1) {
    const current = next[cursor]
    const following = next[cursor + 1]
    if (current.max == null) continue
    following.min = current.max + 1
    if (cursor + 1 < next.length - 1 && following.max != null && following.min > following.max) {
      following.max = following.min
    }
  }
  next[next.length - 1].max = guestMax
  return next
}

export function alignTiersToBands(tiers: TourheroTier[], bands: TourheroOperatorBand[]): TourheroTier[] {
  return bands.map(band => {
    const match = tiers.find(tier => tier.min === band.min && tier.max === band.max)
    return {
      min: band.min,
      max: band.max,
      chargeCents: match?.chargeCents ?? null,
      spots: (match?.spots || []).map(spot => ({ ...spot })),
    }
  })
}

export function rangeLabel(tier: TourheroTier) {
  if (tier.min == null || tier.max == null) return 'Travelers'
  if (tier.min === tier.max) return `${tier.min} travelers`
  return `${tier.min}–${tier.max} travelers`
}

export function isTierLocked(tier: TourheroTier, supplied: TourheroTerms | null | undefined) {
  return suppliedMatch(tier, supplied)?.chargeCents != null
}

export function isSpotChargeLocked(tier: TourheroTier, index: number, supplied: TourheroTerms | null | undefined) {
  return expandedSuppliedSpots(tier, supplied)[index]?.chargeCents != null
}

export function hasLockedSuppliedSpotFrom(
  tier: TourheroTier,
  index: number,
  supplied: TourheroTerms | null | undefined,
) {
  return expandedSuppliedSpots(tier, supplied)
    .slice(index)
    .some(spot => spot.chargeCents != null)
}

function expandedSuppliedSpots(tier: TourheroTier, supplied: TourheroTerms | null | undefined) {
  return (suppliedMatch(tier, supplied)?.spots || []).flatMap(spot => {
    const copies = spot.count != null && spot.count > 1 ? spot.count : 1
    return Array.from({ length: copies }, () => ({
      count: 1,
      chargeCents: spot.chargeCents ?? null,
    }))
  })
}

function suppliedMatch(tier: TourheroTier, supplied: TourheroTerms | null | undefined) {
  return (supplied?.tiers || []).find(suppliedTier => suppliedTier.min === tier.min && suppliedTier.max === tier.max)
}

export function isRoomLocked(supplied: TourheroTerms | null | undefined) {
  return Boolean(supplied?.singleRoomTerm && supplied.singleRoomTerm !== 'unspecified')
}

export function isNoteLocked(supplied: TourheroTerms | null | undefined) {
  return Boolean(supplied?.note)
}

export function spotIsComplete(spot: TourheroSpot) {
  return spot.count != null && spot.count >= 1 && spot.chargeCents != null && spot.chargeCents >= 0
}

export function termsAreComplete(terms: TourheroTerms, guestMin: number, guestMax: number) {
  const tiers = terms.tiers.filter(tier => tier.min != null && tier.max != null)
  if (tiers.length === 0) return false
  if (tiers.some(tier => tier.chargeCents === null || tier.chargeCents === undefined || tier.chargeCents < 0))
    return false
  if (tiers.some(tier => (tier.spots || []).some(spot => spot.chargeCents != null && !spotIsComplete(spot))))
    return false
  if (tiers.some(tier => tier.min! < guestMin || tier.max! > guestMax || tier.max! < tier.min!)) return false

  const sorted = [...tiers].sort((left, right) => left.min! - right.min!)
  for (let index = 1; index < sorted.length; index += 1) {
    if (sorted[index].min! <= sorted[index - 1].max!) return false
  }

  const covered = new Set<number>()
  sorted.forEach(tier => {
    for (let count = tier.min!; count <= tier.max!; count += 1) covered.add(count)
  })
  for (let count = guestMin; count <= guestMax; count += 1) {
    if (!covered.has(count)) return false
  }
  if (covered.size !== guestMax - guestMin + 1) return false

  if (terms.singleRoomTerm === 'included' || terms.singleRoomTerm === 'waived') {
    return columnRoomsAreComplete(terms)
  }
  if (terms.singleRoomTerm === 'supplement') {
    return (
      terms.singleRoomSupplementCents !== null && terms.singleRoomSupplementCents >= 0 && columnRoomsAreComplete(terms)
    )
  }
  return false
}

function columnRoomsAreComplete(terms: TourheroTerms) {
  const width = terms.tiers.reduce((max, tier) => Math.max(max, tier.spots?.length || 0), 0)
  for (let index = 0; index < width; index += 1) {
    const priced = terms.tiers.some(tier => tier.spots[index]?.chargeCents != null)
    if (!priced) continue
    const room = terms.spotRooms[index]
    if (!room || !roomIsComplete(room.singleRoomTerm, room.singleRoomSupplementCents)) return false
  }
  return true
}

function roomIsComplete(term: TourheroRoomTerm, cents: number | null) {
  if (term === 'included' || term === 'waived') return true
  if (term === 'supplement') return cents !== null && cents >= 0
  return false
}

export function columnRoomsFromTiers(tiers: TourheroTier[]): TourheroColumnRoom[] {
  const expanded = tiers.map(tier =>
    (tier.spots || []).flatMap(spot => {
      const copies = spot.count != null && spot.count > 1 ? spot.count : 1
      return Array.from({ length: copies }, () => spot)
    }),
  )
  const width = expanded.reduce((max, spots) => Math.max(max, spots.length), 0)
  return Array.from({ length: width }, (_, index) => {
    for (const spots of expanded) {
      const spot = spots[index]
      if (spot?.singleRoomTerm && spot.singleRoomTerm !== 'unspecified') {
        return {
          singleRoomTerm: spot.singleRoomTerm,
          singleRoomSupplementCents: spot.singleRoomSupplementCents ?? null,
        }
      }
    }
    return { singleRoomTerm: 'unspecified', singleRoomSupplementCents: null }
  })
}

function spotsPayload(spots: TourheroSpot[]) {
  const next = spots.map(spot => ({
    count: spot.count ?? 1,
    charge_cents: spot.chargeCents,
  }))
  while (next.length > 0 && next[next.length - 1].charge_cents == null) next.pop()
  return next
}

export function termsPayload(terms: TourheroTerms) {
  return {
    tiers: terms.tiers.map(tier => ({
      min: tier.min,
      max: tier.max,
      charge_cents: tier.chargeCents,
      spots: spotsPayload(tier.spots || []),
    })),
    single_room_term: terms.singleRoomTerm,
    single_room_supplement_cents: terms.singleRoomTerm === 'supplement' ? terms.singleRoomSupplementCents : null,
    spot_rooms: (terms.spotRooms || []).map(room => ({
      single_room_term: room.singleRoomTerm,
      single_room_supplement_cents: room.singleRoomTerm === 'supplement' ? room.singleRoomSupplementCents : null,
    })),
    note: terms.note,
  }
}

export function roomLabel(term: TourheroRoomTerm) {
  return ROOM_OPTIONS.find(option => option.value === term)?.label || 'Not yet specified'
}
